import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Link } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, User, Calendar, Phone } from "lucide-react";
import useSignUp from "../../hooks/auth/useSignUp";
import { SignUpRequest } from "../../types/auth";
import { useState, KeyboardEvent } from "react";
import { DebouncedInput } from "../../components/DebouncedInput";
import logo from "../../images/aivy_logo3.png";
import useEmailVerification from "../../hooks/auth/useEmailVerification";
import useSmsVerification from "../../hooks/auth/useSmsVerification";

const signUpSchema = z.object({
  email: z
    .string()
    .regex(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      "올바른 이메일 형식이 아닙니다"
    ),
  x_id: z.string().min(1, "아이디를 입력해주세요"),
  app_key: z.string().min(1, "비밀번호를 입력해주세요"),
  nickname: z.string().min(2, "닉네임은 2자 이상이어야 합니다"),
  gender: z.enum(["m", "f"], {
    required_error: "성별을 선택해주세요",
  }),
  birth: z.string().min(1, "생년월일을 입력해주세요"),
  platform: z.string(),
  contact_hp: z
    .string()
    .regex(
      /^\d{3}-\d{4}-\d{4}$/,
      "올바른 전화번호 형식이 아닙니다 (예: 010-1234-5678)"
    ),
  private_agreement: z.boolean().refine((val) => val === true, {
    message: "개인정보 처리방침에 동의해주세요",
  }),
  service_agreeement: z.boolean().refine((val) => val === true, {
    message: "서비스 이용약관에 동의해주세요",
  }),
  marketing_agreement: z.boolean(),
});

const formatBirthDate = (value: string) => {
  value = value.replace(/\D/g, "");

  if (value.length > 4) {
    value = value.slice(0, 4) + "-" + value.slice(4);
  }
  if (value.length > 7) {
    value = value.slice(0, 7) + "-" + value.slice(7);
  }

  return value.slice(0, 10);
};

const formatPhoneNumber = (value: string) => {
  value = value.replace(/\D/g, "");

  if (value.length > 3) {
    value = value.slice(0, 3) + "-" + value.slice(3);
  }
  if (value.length > 8) {
    value = value.slice(0, 8) + "-" + value.slice(8);
  }

  return value.slice(0, 13);
};

const SignUp = () => {
  const { signUp, isLoading, apiError } = useSignUp();
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const {
    verifyEmail,
    isLoading: isVerifying,
    setApiVerificationError,
    authcode,
  } = useEmailVerification();

  const {
    verifySms,
    isLoading: isSmsVerifying,
    verificationError: smsVerificationError,
    setVerificationError: setSmsVerificationError,
    authcode: smsAuthcode,
  } = useSmsVerification();

  const [isEmailVerified, setIsEmailVerified] = useState<boolean>(false);
  const [emailVerificationCode, setEmailVerificationCode] =
    useState<string>("");
  const [emailError, setEmailError] = useState<string>("");
  const [verificationCodeError, setVerificationCodeError] =
    useState<string>("");
  const [isVerificationRequested, setIsVerificationRequested] =
    useState<boolean>(false);

  const [isSmsVerified, setIsSmsVerified] = useState<boolean>(false);
  const [smsVerificationCode, setSmsVerificationCode] = useState<string>("");
  const [isSmsVerificationRequested, setIsSmsVerificationRequested] =
    useState<boolean>(false);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<SignUpRequest>({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      platform: "aivy",
      private_agreement: false,
      service_agreeement: false,
      marketing_agreement: false,
    },
  });

  const privateAgreement = watch("private_agreement");
  const serviceAgreement = watch("service_agreeement");
  const marketingAgreement = watch("marketing_agreement");

  const isAllAgreed =
    privateAgreement && serviceAgreement && marketingAgreement;

  const handleAllAgreementChange = (checked: boolean) => {
    setValue("private_agreement", checked);
    setValue("service_agreeement", checked);
    setValue("marketing_agreement", checked);
  };

  const handleEmailVerification = async () => {
    const email = watch("email");
    if (!email) {
      setEmailError("이메일을 먼저 입력해주세요.");
      return;
    }

    // 이메일 형식 검증
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) {
      setEmailError("올바른 이메일 형식이 아닙니다.");
      return;
    }

    setEmailError("");
    setIsVerificationRequested(true);

    try {
      const result = await verifyEmail({ email });
      if (result?.duplicate) {
        setEmailError("이미 가입된 이메일입니다. 다른 이메일을 사용해주세요.");
        setIsVerificationRequested(false);
      }
    } catch (error) {
      setIsVerificationRequested(false);
    }
  };

  const handleVerificationCodeCheck = () => {
    if (emailVerificationCode === authcode) {
      setIsEmailVerified(true);
      setVerificationCodeError("");
    } else {
      setVerificationCodeError("인증 코드가 일치하지 않습니다.");
    }
  };

  const handleSmsVerification = async () => {
    const phoneNumber = watch("contact_hp");
    if (!phoneNumber) {
      setSmsVerificationError("전화번호를 먼저 입력해주세요.");
      return;
    }

    // 전화번호 형식 검증
    const phoneRegex = /^\d{3}-\d{4}-\d{4}$/;
    if (!phoneRegex.test(phoneNumber)) {
      setSmsVerificationError("올바른 전화번호 형식이 아닙니다.");
      return;
    }

    setSmsVerificationError("");
    setIsSmsVerificationRequested(true);

    try {
      const result = await verifySms({ contact_hp: phoneNumber });
      if (result.isDuplicate) {
        setIsSmsVerificationRequested(false);
      }
    } catch (error) {
      setIsSmsVerificationRequested(false);
    }
  };

  const handleSmsVerificationCodeCheck = () => {
    if (smsVerificationCode === smsAuthcode) {
      setIsSmsVerified(true);
      setSmsVerificationError("");
    } else {
      setSmsVerificationError("인증 코드가 일치하지 않습니다.");
    }
  };

  const handleBirthDateChange = (value: string) => {
    const formatted = formatBirthDate(value);
    setValue("birth", formatted);
  };

  const handlePhoneNumberChange = (value: string) => {
    const formatted = formatPhoneNumber(value);
    setValue("contact_hp", formatted);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const target = e.target as HTMLInputElement;
    if (
      e.key === "Backspace" &&
      target.value.endsWith("-")
    ) {
      e.preventDefault();
      const newValue = target.value.slice(0, -2);
      target.value = newValue;
      setValue(
        target.name as "birth" | "contact_hp",
        newValue
      );
    }
  };

  const onSubmit = async (data: SignUpRequest) => {
    if (!isEmailVerified) {
      setApiVerificationError("이메일 인증을 완료해주세요.");
      return;
    }
    await signUp(data, setValue);
  };

  const handleNavigation = (path: string): void => {
    window.location.href = path;
  };

  const handleOpenInNewTab = (path: string): void => {
    window.open(path, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex flex-col font-serif-kr">
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-1">
              <img src={logo} alt="Aivy Logo" className="w-20 h-20" />
              <button
                onClick={() => handleNavigation("/")}
                className="h-12 text-4xl font-bold bg-gradient-to-r from-teal-600/60 via-green-500 to-emerald-600/60 bg-clip-text text-transparent font-serif-en"
              >
                Aivy AI
              </button>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              새로운 계정 만들기
            </h2>
          </div>

          {apiError && (
            <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm text-center mb-6">
              {apiError}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <label className="block text-sm md:text-base font-medium text-gray-700 mb-2 text-left">
                이메일
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <div className="flex gap-2">
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <DebouncedInput
                        {...field}
                        type="email"
                        placeholder="이메일을 입력하세요"
                        className="pl-12 flex-1"
                        delay={300}
                        disabled={isEmailVerified}
                      />
                    )}
                  />
                  <button
                    type="button"
                    onClick={handleEmailVerification}
                    disabled={
                      isVerifying || isEmailVerified || isVerificationRequested
                    }
                    className="px-4 py-2 bg-black text-white rounded-lg hover:shadow-lg transform transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap min-w-[100px] flex items-center justify-center"
                  >
                    {isVerifying ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : isEmailVerified ? (
                      "인증완료"
                    ) : isVerificationRequested ? (
                      "전송완료"
                    ) : (
                      "인증하기"
                    )}
                  </button>
                </div>
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600 text-left">
                  {errors.email.message}
                </p>
              )}
              {emailError && (
                <p className="mt-1 text-sm text-red-600 text-left">
                  {emailError}
                </p>
              )}
            </div>

            {authcode && !isEmailVerified && (
              <div>
                <label className="block text-sm md:text-base font-medium text-gray-700 mb-2 text-left">
                  인증 코드
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={emailVerificationCode}
                    onChange={(e) => setEmailVerificationCode(e.target.value)}
                    placeholder="인증 코드를 입력하세요"
                    maxLength={6}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={handleVerificationCodeCheck}
                    className="px-4 py-2 bg-black text-white rounded-lg hover:shadow-lg transform transition-all duration-200 min-w-[100px] flex items-center justify-center"
                  >
                    확인
                  </button>
                </div>
                {verificationCodeError && (
                  <p className="mt-1 text-sm text-red-600 text-left">
                    {verificationCodeError}
                  </p>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm md:text-base font-medium text-gray-700 mb-2 text-left">
                연락처
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Phone className="h-5 w-5 text-gray-400" />
                </div>
                <div className="flex gap-2">
                  <Controller
                    name="contact_hp"
                    control={control}
                    render={({ field }) => (
                      <DebouncedInput
                        {...field}
                        type="text"
                        placeholder="010-0000-0000"
                        maxLength={13}
                        className="pl-12 flex-1"
                        delay={300}
                        disabled={isSmsVerified}
                        onChange={(value) => {
                          field.onChange(value);
                          handlePhoneNumberChange(value);
                        }}
                        onKeyDown={handleKeyDown}
                      />
                    )}
                  />
                  <button
                    type="button"
                    onClick={handleSmsVerification}
                    disabled={
                      isSmsVerifying ||
                      isSmsVerified ||
                      isSmsVerificationRequested
                    }
                    className="px-4 py-2 bg-black text-white rounded-lg hover:shadow-lg transform transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap min-w-[100px] flex items-center justify-center"
                  >
                    {isSmsVerifying ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : isSmsVerified ? (
                      "인증완료"
                    ) : isSmsVerificationRequested ? (
                      "전송완료"
                    ) : (
                      "인증하기"
                    )}
                  </button>
                </div>
              </div>
              {errors.contact_hp && (
                <p className="mt-1 text-sm text-red-600 text-left">
                  {errors.contact_hp.message}
                </p>
              )}
              {smsVerificationError && (
                <p className="mt-1 text-sm text-red-600 text-left">
                  {smsVerificationError}
                </p>
              )}
            </div>

            {smsAuthcode && !isSmsVerified && (
              <div>
                <label className="block text-sm md:text-base font-medium text-gray-700 mb-2 text-left">
                  인증 코드
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={smsVerificationCode}
                    onChange={(e) => setSmsVerificationCode(e.target.value)}
                    placeholder="인증 코드를 입력하세요"
                    maxLength={6}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={handleSmsVerificationCodeCheck}
                    className="px-4 py-2 bg-black text-white rounded-lg hover:shadow-lg transform transition-all duration-200 min-w-[100px] flex items-center justify-center"
                  >
                    확인
                  </button>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm md:text-base font-medium text-gray-700 mb-2 text-left">
                아이디
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <Controller
                  name="x_id"
                  control={control}
                  render={({ field }) => (
                    <DebouncedInput
                      {...field}
                      type="text"
                      placeholder="아이디를 입력하세요"
                      className="pl-12"
                      delay={300}
                    />
                  )}
                />
              </div>
              {errors.x_id && (
                <p className="mt-1 text-sm text-red-600 text-left">
                  {errors.x_id.message}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm md:text-base font-medium text-gray-700 mb-2 text-left">
                비밀번호
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <Controller
                  name="app_key"
                  control={control}
                  render={({ field }) => (
                    <DebouncedInput
                      {...field}
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      className="pl-12 pr-12"
                      delay={300}
                    />
                  )}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.app_key && (
                <p className="mt-1 text-sm text-red-600 text-left">
                  {errors.app_key.message}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm md:text-base font-medium text-gray-700 mb-2 text-left">
                닉네임
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <Controller
                  name="nickname"
                  control={control}
                  render={({ field }) => (
                    <DebouncedInput
                      {...field}
                      type="text"
                      placeholder="닉네임을 입력하세요"
                      className="pl-12"
                      delay={300}
                    />
                  )}
                />
              </div>
              {errors.nickname && (
                <p className="mt-1 text-sm text-red-600 text-left">
                  {errors.nickname.message}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm md:text-base font-medium text-gray-700 mb-2 text-left">
                성별
              </label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setValue("gender", "m")}
                  className={`py-3 px-4 rounded-xl border ${
                    watch("gender") === "m"
                      ? "border-purple-500 bg-purple-50 text-purple-700"
                      : "border-gray-200 bg-white text-gray-700 hover:border-purple-200"
                  } transition-all duration-200`}
                >
                  남성
                </button>
                <button
                  type="button"
                  onClick={() => setValue("gender", "f")}
                  className={`py-3 px-4 rounded-xl border ${
                    watch("gender") === "f"
                      ? "border-purple-500 bg-purple-50 text-purple-700"
                      : "border-gray-200 bg-white text-gray-700 hover:border-purple-200"
                  } transition-all duration-200`}
                >
                  여성
                </button>
              </div>
              {errors.gender && (
                <p className="mt-2 text-sm text-red-600 text-left">
                  {errors.gender.message}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm md:text-base font-medium text-gray-700 mb-2 text-left">
                생년월일
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Calendar className="h-5 w-5 text-gray-400" />
                </div>
                <Controller
                  name="birth"
                  control={control}
                  render={({ field }) => (
                    <DebouncedInput
                      {...field}
                      type="text"
                      placeholder="YYYY-MM-DD"
                      maxLength={10}
                      className="pl-12"
                      delay={300}
                      onChange={(value) => {
                        field.onChange(value);
                        handleBirthDateChange(value);
                      }}
                      onKeyDown={handleKeyDown}
                    />
                  )}
                />
              </div>
              {errors.birth && (
                <p className="mt-2 text-sm text-red-600 text-left">
                  {errors.birth.message}
                </p>
              )}
            </div>

            <div className="space-y-3">
              <div className="flex flex-col border-b border-gray-200 pb-3">
                <div className="flex items-center space-x-3 mb-1">
                  <input
                    type="checkbox"
                    checked={isAllAgreed}
                    onChange={(e) => handleAllAgreementChange(e.target.checked)}
                    className="h-5 w-5 accent-green-600 focus:ring-green-500 border-gray-300 rounded"
                    style={{ accentColor: "#059669" }}
                  />
                  <label className="text-base md:text-lg text-gray-900 cursor-pointer">
                    약관 전체 동의
                  </label>
                </div>
                <span className="ml-8 text-xs md:text-sm text-gray-900">
                  선택 사항에 대한 동의를 거부하는 경우에도 서비스는 이용이
                  가능합니다.
                </span>
              </div>

              <div className="ml-6 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      {...register("private_agreement")}
                      className="h-4 w-4 accent-green-600 focus:ring-green-500 border-gray-300 rounded"
                      style={{ accentColor: "#059669" }}
                    />
                    <label className="text-sm md:text-base text-gray-900 cursor-pointer">
                      개인정보 처리방침 동의
                      <span className="text-red-500"> (필수)</span>
                    </label>
                  </div>
                  <span className="text-sm md:text-base text-gray-900 cursor-pointer">
                    <button
                      type="button"
                      className="hover:underline"
                      onClick={() => handleOpenInNewTab("/policy/privacy")}
                    >
                      자세히
                    </button>
                  </span>
                </div>
                {errors.private_agreement && (
                  <p className="text-sm text-red-600 text-left">
                    {errors.private_agreement.message}
                  </p>
                )}

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      {...register("service_agreeement")}
                      className="h-4 w-4 accent-green-600 focus:ring-green-500 border-gray-300 rounded"
                      style={{ accentColor: "#059669" }}
                    />
                    <label className="text-sm md:text-base text-gray-900 cursor-pointer">
                      서비스 이용약관 동의{" "}
                      <span className="text-red-500"> (필수)</span>
                    </label>
                  </div>
                  <span className="text-sm text-gray-900 cursor-pointer">
                    <button
                      type="button"
                      className="hover:underline"
                      onClick={() => handleOpenInNewTab("/policy/terms")}
                    >
                      자세히
                    </button>
                  </span>
                </div>
                {errors.service_agreeement && (
                  <p className="text-sm text-red-600 text-left">
                    {errors.service_agreeement.message}
                  </p>
                )}

                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    {...register("marketing_agreement")}
                    className="h-4 w-4 accent-green-600 focus:ring-green-500 border-gray-300 rounded"
                    style={{ accentColor: "#059669" }}
                  />
                  <label className="text-sm md:text-base text-gray-900 cursor-pointer">
                    마케팅 정보 수신 동의
                  </label>
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading || !isEmailVerified || !isSmsVerified}
              className="w-full py-3 bg-gradient-to-r from-gray-800/70 to-gray-600 text-white font-semibold rounded-xl hover:shadow-lg transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                "회원가입"
              )}
            </button>
          </form>

          <p className="mt-6 text-center text-sm md:text-base text-gray-600">
            이미 계정이 있으신가요?{" "}
            <Link
              to="/auth/signin"
              className="text-purple-600 hover:text-purple-700 font-medium"
            >
              로그인하기
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
